import { Color, CompositeLayer } from "@deck.gl/core";
import {
  IconLayer,
  IconLayerProps,
  ScatterplotLayer,
  ScatterplotLayerProps,
  TextLayer,
  TextLayerProps,
} from "@deck.gl/layers";
import { Texture } from "@luma.gl/core";
import { useContext, useEffect, useMemo, useRef, useState } from "react";

import { CommentRead } from "@nens/rana/api-v1";
import { TenantContext } from "@nens/ranalib";
import { makeUnique, removeNullUndefined } from "@nens/shared-utils";

import { COLORS } from "@/config/deckgl-conts";
import { useProfileImageQueries } from "@/lib/profile";

import { RESOLVED_OPACITY } from "../consts/resolved-opacity";

import { UsePublicationCommentReturn } from "./use-comment";

type IconMapping = IconLayerProps["iconMapping"];

type ProfileLayerPropsHelper<DataT extends object> = {
  getTextColor: TextLayerProps<DataT>["getColor"];
  getIconColor: IconLayerProps<DataT>["getColor"];
  getIconSize: IconLayerProps<DataT>["getSize"];
};

type ProfileLayerProps<DataT extends object> = Omit<
  ScatterplotLayerProps<DataT> &
    TextLayerProps<DataT> &
    IconLayerProps<DataT> &
    ProfileLayerPropsHelper<DataT>,
  "getColor"
>;

class ProfileLayer<T extends object> extends CompositeLayer<Required<ProfileLayerProps<T>>> {
  static override layerName = "ProfileLayer";

  renderLayers() {
    return [
      new ScatterplotLayer(
        this.getSubLayerProps({
          ...this.props,
          id: this.props.id + "-scatterplot",
          data: this.props.data,
        })
      ),
      new TextLayer(
        this.getSubLayerProps({
          // @ts-expect-error to make the default conistent with the scatterplot
          billboard: false,
          ...this.props,
          id: this.props.id + "-text",
          data: this.props.data,
          getColor: this.props.getTextColor,
        })
      ),
      ...(this.props.iconAtlas
        ? [
            new IconLayer(
              this.getSubLayerProps({
                // @ts-expect-error to make the default conistent with the scatterplot
                billboard: false,
                ...this.props,
                id: this.props.id + "-icon",
                data: this.props.data,
                getSize: this.props.getIconSize,
                getColor: this.props.getIconColor,
              })
            ),
          ]
        : []),
    ];
  }
}

type ImageWithCreatorId = { image: string; id: string };

const LARGE_MULTIPLIER = 1.4;

export function usePublicationCommentLayer({
  commentThreads: commentThreadNoCreating,
  publicationId,
  creatingCommentAt,
  selectedCommentThread,
  setSelectedCommentThreadId,
  showResolved,
}: UsePublicationCommentReturn) {
  const { user } = useContext(TenantContext);
  const [[iconAtlas, iconMapping], setIconInfo] = useState<
    [HTMLCanvasElement | undefined, IconMapping | undefined]
  >([undefined, undefined]);

  const commentThreads = useMemo(() => {
    const commentThreads = [...(commentThreadNoCreating ?? [])].filter(({ resolved }) =>
      showResolved ? true : !resolved
    );
    if (creatingCommentAt)
      commentThreads.push({
        id: "",
        publicationId,
        location: {
          type: "Point",
          coordinates: creatingCommentAt,
        },
        // set an initial value for id for User, for Creator it is not needed
        creator: { ...user, id: "id" in user ? user.id : "-" },
        messages: [],
        createdAt: new Date(),
        updatedAt: new Date(),
        apiKeyId: null,
        resolved: false,
      });
    return commentThreads;
  }, [commentThreadNoCreating, creatingCommentAt, publicationId, user, showResolved]);

  const uniqueCreatorsIds = removeNullUndefined(
    makeUnique((commentThreads ?? []).map(({ creator }) => creator.id))
  );

  const profileImages = useProfileImageQueries(uniqueCreatorsIds).map(({ data }) => data);

  // Filter unique creators
  const previousUniqueImageWithCreatorId = useRef<ImageWithCreatorId[] | null>(null);
  const uniqueImageWithCreatorId = profileImages
    .map((image, idx) => ({
      image,
      id: uniqueCreatorsIds[idx],
    }))
    .filter(
      (imageAndCreatorId): imageAndCreatorId is ImageWithCreatorId => !!imageAndCreatorId.image
    );
  useEffect(() => {
    if (
      previousUniqueImageWithCreatorId.current &&
      previousUniqueImageWithCreatorId.current.toString() === uniqueImageWithCreatorId.toString()
    )
      return;
    previousUniqueImageWithCreatorId.current = uniqueImageWithCreatorId;

    if (!uniqueImageWithCreatorId.length) return;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const SIZE = 200;

    canvas.width = SIZE;
    canvas.height = SIZE * uniqueImageWithCreatorId.length;

    const iconMapping: IconMapping = {};

    const imagePromises = uniqueImageWithCreatorId.map(
      ({ image, id }, index) =>
        new Promise<void>((resolve, reject) => {
          const img = new Image();
          img.src = image;

          const y = SIZE * index;
          iconMapping[id] = { x: 0, y, width: SIZE, height: SIZE, mask: false };

          img.onload = () => {
            ctx.save();

            // Create a circular clipping path
            ctx.beginPath();
            const centerX = SIZE / 2;
            const centerY = y + SIZE / 2;
            const radius = SIZE / 2;
            ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
            ctx.clip();

            // Calculate the aspect ratio and scaling for "cover"
            const imgAspectRatio = img.width / img.height;
            const canvasAspectRatio = 1; // Canvas is square

            let drawWidth, drawHeight, offsetX, offsetY;

            if (imgAspectRatio > canvasAspectRatio) {
              // Image is wider, scale based on height
              drawHeight = SIZE;
              drawWidth = SIZE * imgAspectRatio;
              offsetX = (SIZE - drawWidth) / 2;
              offsetY = 0;
            } else {
              // Image is taller, scale based on width
              drawWidth = SIZE;
              drawHeight = SIZE / imgAspectRatio;
              offsetX = 0;
              offsetY = (SIZE - drawHeight) / 2;
            }

            // Draw the image to cover the canvas
            ctx.drawImage(img, offsetX, y + offsetY, drawWidth, drawHeight);

            // Restore the context for the next image
            ctx.restore();
            resolve();
          };

          img.onerror = reject;
        })
    );

    Promise.all(imagePromises).then(() => setIconInfo([canvas, iconMapping]));
  }, [uniqueImageWithCreatorId]);

  return useMemo(() => {
    const getSizeWithMultiplier = (id: string, size: number) =>
      selectedCommentThread?.id === id || !id ? size * LARGE_MULTIPLIER : size;

    const getColorWithOpacity = (color: Color, resolved: boolean): Color => [
      color[0],
      color[1],
      color[2],
      resolved ? 255 * RESOLVED_OPACITY : 255,
    ];

    return new ProfileLayer<CommentRead>({
      // All
      id: "scatterplot-text-layer",
      data: commentThreads,
      pickable: true,
      stroked: true,
      filled: true,
      getPosition: (d) => d.location.coordinates as [number, number],

      // Scatterplot
      radiusUnits: "pixels",
      lineWidthUnits: "pixels",
      getRadius: ({ id }) => getSizeWithMultiplier(id, 15),
      radiusScale: 1,
      getFillColor: ({ resolved }) => getColorWithOpacity(COLORS.GLACIER, resolved),
      getLineColor: ({ resolved }) => getColorWithOpacity(COLORS.BLUE_ANALYTIC, resolved),
      getLineWidth: 2,

      // Text
      getText: (d) => d.creator.givenName[0] + d.creator.familyName[0],
      getAlignmentBaseline: "center",
      getTextColor: ({ resolved }) => getColorWithOpacity(COLORS.BLUE_ANALYTIC, resolved),
      getSize: ({ id }) => getSizeWithMultiplier(id, 15),
      getTextAnchor: "middle",
      fontWeight: 500,

      // Icon
      iconAtlas: iconAtlas as unknown as Texture,
      iconMapping,
      sizeUnits: "pixels",
      getIcon: ({ creator }) => creator.id ?? "",
      getIconSize: ({ id }) => getSizeWithMultiplier(id, 28),
      getIconColor: ({ resolved }) => getColorWithOpacity([0, 0, 0], resolved),

      // Listeners
      onClick: (e) => {
        setSelectedCommentThreadId(e.object.id === selectedCommentThread?.id ? null : e.object.id);
      },

      updateTriggers: {
        getRadius: selectedCommentThread,
        getSize: selectedCommentThread,
        getIconSize: selectedCommentThread,
        getIcon: [iconAtlas, iconMapping],
      },
    });
  }, [commentThreads, setSelectedCommentThreadId, iconAtlas, iconMapping, selectedCommentThread]);
}
